import type { RefObject } from "react";

import { fetchPublicAPI } from "hooks/usePublicAPI";

/** How long a visitor waits for a token before the step counts as failed. The
 * widget's own promise never settles when its script did not load. */
export const RECAPTCHA_TOKEN_TIMEOUT_MS = 8_000;

/** The part of the invisible widget a token request uses. */
export type RecaptchaWidget = {
  reset(): void;
  executeAsync(): Promise<string | null>;
};

export type RecaptchaFailureReason =
  "not_ready" | "timeout" | "no_token" | "errored";

export type RecaptchaTokenResult =
  { ok: true; token: string } | { ok: false; reason: RecaptchaFailureReason };

function failure(reason: RecaptchaFailureReason): RecaptchaTokenResult {
  return { ok: false, reason };
}

async function executeFresh(
  widget: RecaptchaWidget,
): Promise<RecaptchaTokenResult> {
  try {
    widget.reset();
    const token = await widget.executeAsync();
    return token ? { ok: true, token } : failure("no_token");
  } catch {
    return failure("errored");
  }
}

/**
 * Asks the widget for a token that has not been used before: it resets the
 * widget, then executes it, so a token from an earlier send is never reused or
 * waited on. Never rejects. Settles within `timeoutMs`; a request still
 * pending after that is abandoned and its late answer ignored.
 */
export async function freshRecaptchaToken(
  ref: RefObject<RecaptchaWidget | null>,
  { timeoutMs = RECAPTCHA_TOKEN_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<RecaptchaTokenResult> {
  const widget = ref.current;
  if (widget === null) return failure("not_ready");

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<RecaptchaTokenResult>((resolve) => {
    timer = setTimeout(() => resolve(failure("timeout")), timeoutMs);
  });
  try {
    return await Promise.race([executeFresh(widget), timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

type RecaptchaConfigResponse = { recaptchaSiteKey?: string };

let siteKeyRequest: Promise<string> | null = null;

/** The site key, fetched once and shared by every caller; a failed fetch is
 * not remembered, so the next call tries again. */
export function loadRecaptchaSiteKey(): Promise<string> {
  if (siteKeyRequest !== null) return siteKeyRequest;
  const request = fetchPublicAPI<RecaptchaConfigResponse>(
    "/auth/recaptcha-config",
  ).then((data) => {
    if (!data.recaptchaSiteKey) throw new Error("recaptchaSiteKey missing");
    return data.recaptchaSiteKey;
  });
  siteKeyRequest = request;
  request.catch(() => {
    if (siteKeyRequest === request) siteKeyRequest = null;
  });
  return request;
}

/** Must be the address react-google-recaptcha requests its script from; a
 * different one only wastes the preload. */
const RECAPTCHA_SCRIPT_URL =
  "https://www.google.com/recaptcha/api.js?onload=onloadcallback&render=explicit";

function preloadRecaptchaScript(): void {
  const selector = `link[rel="preload"][href="${RECAPTCHA_SCRIPT_URL}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "script";
  link.href = RECAPTCHA_SCRIPT_URL;
  document.head.appendChild(link);
}

/** Starts the downloads a code request will need, without waiting for
 * anything: the script and the site key. Safe to call repeatedly. */
export function startRecaptchaPreload(): void {
  preloadRecaptchaScript();
  loadRecaptchaSiteKey().catch(() => undefined);
}
